import store from '@/store'
import { useFeatureFlags } from '@/composables/useFeatureFlags'
import onboardingService from '@/services/onboardingService'
import { useOnboardingDraft } from '@/composables/useOnboardingDraft'
import { ONBOARDING_STATUS } from '@/constants/onboarding'

/**
 * Buyer tenants never register as suppliers; see the guard in router/index.js, which
 * resolves the same edition id.
 */
const BUYER_EDITION_ID = '98CAF202-9D8E-B62F-01C1-39FF3E9F71A0'

/**
 * Statuses that mean the supplier still has registration work to do.
 *
 * MoreInfoRequested is in the list on purpose: an admin sent the draft back, and landing
 * that supplier on their profile with a notification they may never open is how a
 * registration sits untouched for a fortnight.
 */
const UNFINISHED_STATUSES = [
  ONBOARDING_STATUS.REGISTERED,
  ONBOARDING_STATUS.CONTACT_VERIFIED,
  ONBOARDING_STATUS.MORE_INFO_REQUESTED
]

/**
 * Whether a supplier should be taken to the registration flow rather than the app.
 *
 * Built as a one-shot call rather than a router guard: the status only changes when the
 * supplier acts on it, and a guard would re-fetch it on every navigation to answer a
 * question that matters once, at sign-in.
 */
export function useOnboardingGate() {
  /**
   * Where a supplier should land after signing in.
   *
   * @param {string} fallback where to go when registration is finished
   * @returns {Promise<string>}
   */
  const resolveLandingRoute = async (fallback = '/profile') => {
    // Dormant until enforcement is switched on: otherwise every existing supplier is
    // dropped into a registration flow they never started, on every sign-in.
    if (!useFeatureFlags().features.onboardingEnforcement) return fallback

    try {
      const draft = await onboardingService.getDraft()
      // Same rule as the confinement check: no registration, no redirect.
      if (draft?.isRegistrationStarted !== true) return fallback
      return UNFINISHED_STATUSES.includes(draft?.status) ? '/onboarding' : fallback
    } catch (err) {
      // A supplier who cannot be routed to their registration must still be able to use
      // the app. The banner picks them up on the next screen either way.
      //
      // Warned rather than swallowed: the fallback is indistinguishable from "registration
      // already finished" on screen, and that ambiguity is expensive to diagnose.
      console.warn('[onboarding] draft could not be read; landing on the fallback route', err)
      return fallback
    }
  }

  const isUnfinished = status => UNFINISHED_STATUSES.includes(status)

  /**
   * Whether this account is confined to the registration flow.
   *
   * Anything short of Verified is confined: unfinished, submitted and waiting, sent back
   * for more information, or rejected. An account that has not been approved has no
   * business anywhere else in the product.
   *
   * Two exemptions, both deliberate:
   *  - A super admin has no registration of their own; confining them would lock the
   *    platform's own staff out of the screens they support, including the queue that
   *    approves everyone else.
   *  - A buyer tenant never goes through supplier registration, so its draft reads as
   *    "Registered" forever and it would be shut out of its own product.
   *
   * The draft load is cached by useOnboardingDraft, so only the first navigation pays.
   */
  const isConfinedToOnboarding = async () => {
    if (!useFeatureFlags().features.onboardingEnforcement) return false

    const currentUser = store.getters['auth/currentUser']
    if (currentUser?.isSuperAdmin) return false
    if (currentUser?.editionId?.toUpperCase() === BUYER_EDITION_ID) return false

    const { load, status, isRegistrationStarted } = useOnboardingDraft()
    await load()

    // Tenants that predate the registration flow have no draft and are never confined.
    // This is what separates "signed up under the new flow" from "has been trading here
    // for two years" — status alone reads "Registered" for both.
    if (!isRegistrationStarted.value) return false

    // A draft that could not be read leaves status null. Not confining then is the safe
    // failure: a backend hiccup must not lock every supplier out of the whole product.
    return status.value !== null && status.value !== ONBOARDING_STATUS.VERIFIED
  }

  return { resolveLandingRoute, isUnfinished, isConfinedToOnboarding, UNFINISHED_STATUSES }
}
